// Right-rail stack of active conversations. Click → focus + navigate.

import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useConversationsStore, type Conversation } from '../stores/conversations';
import { formatDateTime } from '../lib/datetime';
import styles from './ActiveStack.module.css';

export function ActiveStack() {
  // Stable Map → derived array. Returning Array.from() from a Zustand
  // selector creates a fresh array each call and triggers infinite renders.
  const byId = useConversationsStore((s) => s.byId);
  const dismissed = useConversationsStore((s) => s.dismissed);
  const dismiss = useConversationsStore((s) => s.dismiss);
  const conversations = useMemo(
    () =>
      Array.from(byId.values())
        .filter((c) => !dismissed.has(c.sessionId))
        // Newest activity first — uses the most recent message's sentAt, or
        // falls back to startedAt for conversations with no messages yet.
        .sort((a, b) => activityAt(b) - activityAt(a)),
    [byId, dismissed],
  );
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  return (
    <>
      <hr className={styles.rule} data-label="ACTIVE" />
      {conversations.length === 0 ? (
        <p className={styles.empty}>
          No active conversations.
          <span className={styles.emptyHint}>
            Start one from the monitor — chat or video.
          </span>
        </p>
      ) : (
        <ul className={styles.list}>
          {conversations.map((c) => (
            <li key={c.sessionId} className={styles.itemWrap}>
              <button
                type="button"
                className={[styles.item, c.sessionId === id ? styles.itemActive : ''].join(' ')}
                onClick={() => navigate(`/conversation/${c.sessionId}`)}
              >
                <span className={styles.meta}>
                  <span className={styles.medium} data-medium={c.medium}>
                    {c.medium}
                  </span>
                  <span className={styles.status} data-status={c.status ?? 'open'}>
                    {c.status ?? 'open'}
                  </span>
                  {(c.priority === 'high' || c.priority === 'urgent') ? (
                    <span className={styles.priority} data-priority={c.priority}>
                      {c.priority}
                    </span>
                  ) : null}
                  <span className={styles.unread}>
                    <UnreadDot conversation={c} />
                  </span>
                </span>
                <span className={styles.name}>{c.visitorName}</span>
                <span className={styles.preview}>{previewOf(c)}</span>
                {c.tags?.length ? (
                  <span className={styles.tags}>{c.tags.slice(0, 3).map((tag) => `#${tag}`).join(' ')}</span>
                ) : null}
                <span className={styles.timestamp}>{formatDateTime(activityAt(c))}</span>
              </button>
              <button
                type="button"
                className={styles.dismiss}
                aria-label={`Dismiss conversation with ${c.visitorName} from the rail`}
                onClick={(e) => {
                  e.stopPropagation();
                  dismiss(c.sessionId);
                }}
                title="Dismiss (re-pins on new message)"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function activityAt(c: Conversation): number {
  const last = c.messages[c.messages.length - 1];
  return Math.max(c.startedAt, last?.sentAt ?? 0);
}

function UnreadDot({ conversation }: { conversation: Conversation }) {
  // For Phase 2 we just show a dot if there are any visitor messages —
  // Phase 3 will track read state properly.
  const hasVisitor = conversation.messages.some((m) => m.author === 'visitor');
  if (!hasVisitor) return null;
  return <span className={styles.dot} aria-hidden />;
}

function previewOf(c: Conversation): string {
  const last = c.messages[c.messages.length - 1];
  if (!last) return c.medium === 'video' ? 'video call · waiting…' : 'no messages yet';
  return last.text.slice(0, 64) || (last.author === 'visitor' ? 'attachment' : '');
}
